/**
 * The public changelog at /updates: what changed on the site and in which games, newest first.
 * Written for players and parents (never other games' names). Add an entry whenever a deploy changes
 * something people will notice. `games` are catalog ids, shown as links to those games.
 */
export interface Update {
  /** YYYY-MM-DD, Pacific time. */
  date: string;
  title: string;
  items: string[];
  games?: string[];
}

export const UPDATES: Update[] = [
  {
    date: '2026-11-04',
    title: 'Ten games got a makeover, and Touchdown Rush is rebuilt',
    items: [
      'Touchdown Rush is now a fast 5 on 5 arcade football game. You play every snap on offense and defense: pick a play, throw, stiff-arm and dive for big tackles. Play the computer, a friend on the same keyboard, or a full Franchise season.',
      'Tic-Tac-Toe, Four in a Row and Five in a Row now look like real toy boards, with pieces that pop and drop in, a preview of your move and a celebration when someone wins.',
      'Dots and Boxes has a glowing board where captured boxes fill with little critters. Memory Match has bright flip cards. Sea Battle has a living ocean with cartoon boats, splashes and ships that tip over when they sink.',
      'Classic Snake is a googly-eyed snake in a garden. Paddle Duel plays in a glowing arena (the black and white look is one click away). Aim Trainer is now a fairground shooting gallery.',
      'Element Mixer moved to a starry mixing table with colourful elements, a burst for every new discovery and a list you can swipe on phones.',
      'When a round ends, the bar under the game now suggests three other games to try next.',
    ],
    games: ['touchdown-rush', 'tic-tac-toe', 'four-in-a-row', 'gomoku', 'dots-and-boxes', 'memory-match', 'sea-battle', 'classic-snake', 'paddle-duel', 'aim-trainer', 'element-mixer'],
  },
  {
    date: '2026-11-03',
    title: 'New: Volt Trails and Touchdown Rush, a new look for Hush Hotel and a new logo',
    items: [
      'New: Volt Trails, a 3D electric dirt bike game. Pull long wheelies, flip off dirt jumps, ride the pump track and skatepark, try the endless Wheelie Challenge and race 4 riders on 3 trails.',
      'New: Touchdown Rush, a pixel art football game where you are the quarterback and the coach. Throw deep, juke tacklers, kick field goals and take your team through a 7 game season and the playoffs.',
      'Say hello to our new mascot: SuperFun has a fuzzy blue monster logo, and the site got colors to match.',
      'Hush Hotel got a full makeover: rooms change as you climb (parlor, dining room, kitchen, ballroom, greenhouse, bath house, attic and boiler room), with moonlit windows, rain on the glass, lamp light and darker corners where the flashlight helps.',
      'Hush Hotel ghosts are easier to see coming: watch for the glow at the door, the purple light under it and the rattling handle.',
      'The homepage now starts with Jump Back In, the games you played last on this computer, so you can pick up where you left off.',
    ],
    games: ['volt-trails', 'touchdown-rush', 'hush-hotel'],
  },
  {
    date: '2026-10-31',
    title: 'Game guides, strategy tips and new articles',
    items: [
      'Every game page now has a fuller guide: what is inside the game, a strategy section that explains how to get better, and answers to common questions.',
      'New Guides section with longer articles, such as the best games for two players on one keyboard and a full strategy guide for Brainrot Heist.',
      'Category pages explain which games suit younger kids, quick breaks, long sessions and two players.',
      'This What\'s New page, so you can see what changed and when.',
    ],
    games: ['brainrot-heist'],
  },
  {
    date: '2026-10-25',
    title: 'Clearer names and tidier links',
    items: [
      'Hill Rider\'s modes are now called Endless Hills and Crazy Courses.',
      'Old or mistyped addresses now lead to a proper "page not found" message, and the old Sandbox address takes you to Survival games.',
      'Our privacy policy now explains problem reports and children\'s privacy in more detail.',
    ],
    games: ['hill-rider'],
  },
  {
    date: '2026-10-23',
    title: 'Three new survival games, five sports rebuilds and phone fixes',
    items: [
      'New: Disaster Island. Survive rounds of 8 cartoon disasters in a town of breakable blocky buildings, with 10 bots.',
      'New: Raft Drift. Drift across the ocean in 3D, hook floating debris, build your raft, purify water and fend off a cartoon shark.',
      'New: Desert Rails. Keep a steam train running across 80 km of desert: feed the boiler, loot towns and survive the nights.',
      'Rebuilt in 3D: Tennis (timing shots, 10 characters with special shots, real scoring), Ice Hockey (3 on 3 with one-timers and diving goalies), Snowball Fight (team battles and snow walls) and Bumper Cars (shrinking arenas with hazards).',
      'Rebuilt: Tug of War (rhythm pulling, 1v1 up to 3v3), Air Hockey (spin physics and a tournament), Slime Volley (a tour of rivals) and Paddle Duel (classic mode plus power-ups and a 4-way mode).',
      'Phones: Air Hockey, Tennis, Tug of War, Slime Volley and Paddle Duel got proper portrait and landscape layouts with touch controls that stay clear of the action.',
      'Every game now has a short highlight clip that plays when you hover over it.',
      'New Random button (rolls you into a random game) and a Favorites list at the top right of every page.',
      'Games no longer fail to load in a tab that was opened before an update.',
    ],
    games: ['disaster-island', 'raft-drift', 'desert-rails', 'tennis', 'ice-hockey', 'snowball-fight', 'bumper-cars', 'tug-of-war', 'air-hockey', 'slime-volley', 'paddle-duel'],
  },
  {
    date: '2026-10-22',
    title: 'Over 70 games rebuilt from the ground up',
    items: [
      'We rebuilt most of the site\'s games with deeper gameplay, smarter computer players, more levels and modes, and lockers of unlockable looks. Many moved to full 3D.',
      'In 3D now: Block World, Kart Racer, Basketball, Drift King, Turbo Ball, Rail Runner, Obby Tower, Soccer, Sunset Roll, Penalty Kicks, Wobble Race, Blocky Blaster, Ink Turf, Baseball and Bowling.',
      'New: Goo Fort (build towers by day, hold off goo waves at night) and Hush Hotel (a not-too-scary hotel with 100 doors, puzzles and six ghosts to outsmart).',
      'Bigger rebuilds include Cube Dash (10 music levels), Moto Stunts (24 levels), Hill Rider (six endless stages), Bomb Blast (an 8-stage adventure), Super Hop (16 stages), Brick Breaker (32 levels and bosses) and Element Mixer (over 600 elements).',
      'Survival is now its own category, and every game has a new cover.',
      'Game pages got a like button with a count, favorites, a copy-link button, a Play Next column and a Report a Problem button.',
      'A new navy look with rows of games you can scroll sideways.',
    ],
    games: ['block-world', 'kart-racer', 'basketball', 'drift-king', 'turbo-ball', 'rail-runner', 'obby-tower', 'soccer', 'sunset-roll', 'goo-fort', 'hush-hotel', 'cube-dash', 'moto-stunts', 'element-mixer'],
  },
  {
    date: '2026-10-16',
    title: 'Powder Dash and a 3D Brainrot Heist',
    items: [
      'New: Powder Dash, a 3D downhill sled run.',
      'Brainrot Heist rebuilt in 3D: grab brainrots off the carpet, steal from rival bases and lock your own.',
    ],
    games: ['powder-dash', 'brainrot-heist'],
  },
  {
    date: '2026-10-15',
    title: 'Storm Royale in 3D and smarter search',
    items: [
      'Storm Royale rebuilt in full 3D: drop onto the island, loot foam blasters, build walls and outlast 15 bots.',
      'Search understands the names kids actually use for games, and forgives typos.',
      '26 covers redrawn so each one shows what kind of game it is.',
    ],
    games: ['storm-royale'],
  },
  {
    date: '2026-10-13',
    title: 'Categories, favorites and Suggest a Game',
    items: [
      'Category pages for shooting, sports, racing, arcade, clicker, survival, party, board and strategy games, plus singleplayer and 2-player lists.',
      'Save games to your Favorites with the heart button.',
      'Suggest a Game: tell us what you want us to make next.',
      'A page for parents and teachers explaining how the site keeps kids safe.',
      'The Popular row now comes from what people really play.',
    ],
  },
  {
    date: '2026-10-12',
    title: 'Guides on every game page',
    items: [
      'Every game page now explains what the game is about, how to play it on keyboard, mouse and touch, and a few tips.',
    ],
  },
  {
    date: '2026-10-07',
    title: 'New covers for every game',
    items: ['Every one of the 105 games at the time got its own illustrated cover art.'],
  },
  {
    date: '2026-10-04',
    title: 'SuperFunUBG opens',
    items: ['playsuperfun.com launches with over 100 free games that run in the browser: no downloads, no accounts and no chat.'],
  },
];
